import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { EOpenBankingError, isJointApplicant } from '@utils';
import { useApplicationData } from './AppProvider';

type OpenBankingIncomeSyncState =
  | { status: 'idle' | 'loading' | 'success' }
  | { status: 'error'; errorType: EOpenBankingError | undefined };

type OpenBankingStepsContextType = {
  isOpenBankingConsentComplete: boolean;
  setOpenBankingConsentComplete: (value: boolean) => void;
  isRequestingManualIncomeEdit: boolean;
  setIsRequestingManualIncomeEdit: (value: boolean) => void;
  openBankingIncomeSyncState: OpenBankingIncomeSyncState;
  setOpenBankingIncomeSyncState: (state: OpenBankingIncomeSyncState) => void;
};

export const OpenBankingStepsContext = createContext<OpenBankingStepsContextType | null>(null);

export const OpenBankingStepsProvider = ({ children }: { children: ReactNode }) => {
  const { application } = useApplicationData(['application']);
  const isJoint = isJointApplicant();
  const applicant = isJoint ? application?.jointApplicant : application?.primaryApplicant;
  const [isOpenBankingConsentComplete, setOpenBankingConsentComplete] = useState<boolean>(false);
  const [isRequestingManualIncomeEdit, setIsRequestingManualIncomeEdit] = useState<boolean>(false);
  const [openBankingIncomeSyncState, setOpenBankingIncomeSyncState] = useState<OpenBankingIncomeSyncState>({
    status: 'idle',
  });

  useEffect(() => {
    setOpenBankingConsentComplete(!!applicant?.usingOpenBanking);
  }, [applicant?.usingOpenBanking]);

  return (
    <OpenBankingStepsContext.Provider
      value={{
        isOpenBankingConsentComplete,
        isRequestingManualIncomeEdit,
        openBankingIncomeSyncState,
        setOpenBankingConsentComplete,
        setIsRequestingManualIncomeEdit,
        setOpenBankingIncomeSyncState,
      }}
    >
      {children}
    </OpenBankingStepsContext.Provider>
  );
};

export const useOpenBankingSteps = () => {
  const context = useContext(OpenBankingStepsContext);
  if (!context) {
    throw new Error('useOpenBankingSteps must be used within a OpenBankingStepsProvider');
  }
  return context;
};
